import React, { useState, useMemo } from 'react';
import { articles } from '../helpers/articles';

const CategoryFilter = ({ categories, activeCategory, setCategory }) => (
    <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide px-1">
        <button 
            onClick={() => setCategory('Semua')} 
            className={`px-4 py-2 text-xs font-bold rounded-full whitespace-nowrap transition-all duration-300 border ${
                activeCategory === 'Semua' 
                ? 'bg-amber-500 text-white border-amber-600 shadow-[0_4px_15px_rgba(245,158,11,0.4)]' 
                : 'bg-white/50 backdrop-blur-md text-gray-600 border-white/80 hover:bg-white'
            }`}
        >
            Semua
        </button>
        {categories.map(cat => (
             <button 
                key={cat} 
                onClick={() => setCategory(cat)} 
                className={`px-4 py-2 text-xs font-bold rounded-full whitespace-nowrap transition-all duration-300 border ${
                    activeCategory === cat 
                    ? 'bg-amber-500 text-white border-amber-600 shadow-[0_4px_15px_rgba(245,158,11,0.4)]' 
                    : 'bg-white/50 backdrop-blur-md text-gray-600 border-white/80 hover:bg-white'
                }`}
            >
                {cat}
            </button>
        ))}
    </div>
);

export default function EducationScreen() {
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [activeCategory, setActiveCategory] = useState('Semua');

    const categories = useMemo(() => [...new Set(articles.map(a => a.category))], []);
    const filteredArticles = useMemo(() => {
        if (activeCategory === 'Semua') return articles;
        return articles.filter(a => a.category === activeCategory);
    }, [activeCategory]);

    if (selectedArticle) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] font-sans pb-28 pt-8 px-5 relative">
                 <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-[10%] left-[-20%] w-[60vw] h-[60vw] bg-amber-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30"></div>
                    <div className="absolute bottom-[-10%] right-[-20%] w-[70vw] h-[70vw] bg-orange-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30"></div>
                    <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
                </div>

                <div className="relative z-10 max-w-md mx-auto space-y-4">
                    <button 
                        onClick={() => setSelectedArticle(null)} 
                        className="inline-flex items-center space-x-2 text-amber-600 font-bold bg-white/60 backdrop-blur-md px-4 py-2 rounded-xl shadow-sm border border-white hover:bg-white transition-colors"
                    >
                        <span>&larr;</span>
                        <span>Kembali</span>
                    </button>
                    <div className="bg-white/70 backdrop-blur-2xl rounded-[1.5rem] p-6 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <div className="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-lg uppercase tracking-wider mb-4 border border-amber-200">
                            {selectedArticle.category}
                        </div>
                        <h2 className="text-2xl font-extrabold text-gray-800 mb-6 tracking-tight leading-snug">{selectedArticle.title}</h2>
                        
                        {/* Styled Content for Article */}
                        <div className="article-content max-w-none text-gray-700 leading-relaxed" 
                             dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                    </div>
                </div>
                <style>{`
                    .article-content h3 { font-size: 1.125rem; font-weight: 800; color: #1f2937; margin-top: 1.5rem; margin-bottom: 0.75rem; }
                    .article-content p { margin-bottom: 1rem; font-size: 0.875rem; color: #4b5563; line-height: 1.625; }
                    .article-content ul { list-style-type: disc; padding-left: 1.25rem; margin-bottom: 1rem; color: #4b5563; font-size: 0.875rem; }
                    .article-content li { margin-bottom: 0.25rem; }
                    .article-content strong { color: #1f2937; font-weight: 700; }
                `}</style>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-amber-200 pb-28 pt-10 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-[-20%] w-[60vw] h-[60vw] bg-amber-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[40%] right-[-20%] w-[50vw] h-[50vw] bg-orange-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[10%] w-[70vw] h-[70vw] bg-yellow-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto space-y-6">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/60 backdrop-blur-md rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                        📚
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">Pusat Edukasi</h2>
                    <p className="text-sm text-gray-500 mt-2">Artikel dan panduan kesehatan anak</p>
                </div>

                <div className="sticky top-4 z-20">
                    <CategoryFilter categories={categories} activeCategory={activeCategory} setCategory={setActiveCategory} />
                </div>

                <div className="space-y-4 mt-6">
                    {filteredArticles.map(article => (
                        <div 
                            key={article.id} 
                            className="group cursor-pointer bg-white/50 backdrop-blur-xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-lg hover:border-amber-300 transition-all duration-300 transform hover:-translate-y-1"
                            onClick={() => setSelectedArticle(article)}
                        >
                            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1.5">{article.category}</p>
                            <h3 className="text-lg font-extrabold text-gray-800 leading-tight mb-2 group-hover:text-amber-600 transition-colors">{article.title}</h3>
                            <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{article.summary}</p>
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
}
